<template>
  <page-layout>
    <template #header>
      <page-header :title="t('roles')">
        <template #actions>
          <Link
            class="flex items-center space-x-2 group"
            :href="route('roles.create')"
          >
            <PlusCircleIcon
              class="text-green-500 w-8 h-8 group-hover:text-green-400"
            />
            <p class="text-gray-700">
              {{ t("new_role") }}
            </p>
          </Link>
        </template>
      </page-header>
    </template>

    <template #content>
      <div class="space-y-8 max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="role in roles.data" :key="role.id">
              <Link
                :href="
                  route('roles.edit', {
                    role: role.id,
                  })
                "
                class="block hover:bg-gray-50"
              >
                <div class="px-4 py-4 sm:px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm font-medium text-light-blue-600 truncate">
                      {{ role.name }}
                    </p>
                  </div>
                  <div class="mt-2 sm:flex sm:justify-between">
                    <div class="sm:flex">
                      <p
                        class="
                          mt-2
                          flex flex-wrap
                          items-center
                          text-xs text-gray-500
                          sm:mt-0
                          gap-3
                          max-w-2xl
                        "
                      >
                        <span
                          class="flex items-center"
                          v-for="permission in role.permissions.data"
                          :key="permission.id"
                        >
                          <CheckCircleIcon
                            class="flex-shrink-0 mr-1 h-3 w-3 text-green-400"
                          />
                          {{ t(permission.name) }}
                        </span>
                      </p>
                    </div>
                    <div
                      class="
                        mt-2
                        flex
                        items-center
                        text-sm text-gray-500
                        sm:mt-0
                      "
                    >
                      {{
                        t("employees", {
                          number: role.employees.data.length,
                        })
                      }}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          </ul>
        </div>
        <teleport to="#modals">
          <role-creation
            v-if="permissions"
            :permissions="permissions"
            @toggleVisibility="creatingRole = !creatingRole"
            :on="creatingRole"
          />
          <role-edit
            v-if="role"
            :permissions="permissions"
            @toggleVisibility="editingRole = !editingRole"
            :on="editingRole"
            :role="role"
          />
        </teleport>
      </div>
    </template>
  </page-layout>
</template>

<script>
import { PlusCircleIcon, CheckCircleIcon } from "@heroicons/vue/solid";
import PageHeader from "@/Shared/Organisms/PageHeader";
import AppLayout from "@/Layouts/AppLayout";
import Pagination from "@/Shared/Molecules/Pagination";
import PageLayout from "@/Layouts/PageLayout";
import RoleCreation from "./Roles/RoleCreation";
import RoleEdit from "./Roles/RoleEdit";
import { useI18n } from "vue-i18n";

export default {
  layout: AppLayout,

  props: {
    role: Object,
    roles: Object,
    permissions: Object,
    openCreateRole: {
      type: Boolean,
      default: false,
    },
    openEditRole: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    PageLayout,
    Pagination,
    PageHeader,
    AppLayout,
    RoleCreation,
    RoleEdit,
    PlusCircleIcon,
    CheckCircleIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  created() {
    if (this.openCreateRole) {
      this.creatingRole = true;
    }

    if (this.openEditRole) {
      this.editingRole = true;
    }
  },

  data() {
    return {
      creatingRole: false,
      editingRole: false,
    };
  },

  watch: {
    openEditRole: function (openEditRole) {
      if (openEditRole) {
        this.editingRole = true;
      }
    },
  },
};
</script>

<i18n>
{
  "es": {
    "roles": "Roles y permisos",
    "new_role": "Nuevo rol",
    "employees": "{number} empleado(s)",
    "manage staff": "Administrar personal y permisos",
    "manage customers": "Administrar clientes",
    "manage services": "Administrar servicios",
    "manage equipment": "Administrar equipos de medición",
  }
}
</i18n>
